import { useSnackbar } from "notistack"
import type { AxiosError } from "axios"
import _ from "lodash"
import { useCallback } from "react"
import { ApiError } from "@/api/client"
import { Stack } from "@mui/material"
import BbText from "@/components/building-blocks/text/BbText"

const getMessageContent = (line1?: string, line2?: string) => (
    <Stack direction="column">
        {line1 && (
            <BbText fontWeight={line2 ? "bold" : undefined} fontSize={line2 ? "110%" : undefined}>
                {line1}
            </BbText>
        )}
        {line2 && <BbText>{line2}</BbText>}
    </Stack>
)

export const useResponseNotification = () => {
    const { enqueueSnackbar } = useSnackbar()

    const handleErrorResponse = useCallback(
        (res: AxiosError<ApiError> | string | undefined, msg?: string) => {
            if (_.isString(res)) {
                enqueueSnackbar(getMessageContent(msg, res), {
                    variant: "error",
                    anchorOrigin: {
                        vertical: "top",
                        horizontal: "center",
                    },
                    preventDuplicate: true,
                })
            } else if (res) {
                const { message } = (res as AxiosError<ApiError>).response?.data as ApiError
                if (message)
                    enqueueSnackbar(getMessageContent(msg, message), {
                        variant: "error",
                        anchorOrigin: {
                            vertical: "top",
                            horizontal: "center",
                        },
                        preventDuplicate: true,
                    })
                else
                    enqueueSnackbar(getMessageContent(msg, res?.message), {
                        variant: "error",
                        anchorOrigin: {
                            vertical: "top",
                            horizontal: "center",
                        },
                        preventDuplicate: true,
                    })
            } else {
                if (msg)
                    enqueueSnackbar(msg, {
                        variant: "error",
                        anchorOrigin: {
                            vertical: "top",
                            horizontal: "center",
                        },
                        preventDuplicate: true,
                    })
            }
        },
        [enqueueSnackbar]
    )

    const handleSuccessResponse = useCallback(
        (title: string) => {
            enqueueSnackbar(title, {
                variant: "success",
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "center",
                },
            })
        },
        [enqueueSnackbar]
    )

    const handleMessage = useCallback(
        (
            title: string,
            status: "success" | "error" | "warning" | "info" = "info",
            duration = 5000,
            onClose?: () => void
        ) => {
            enqueueSnackbar(title, {
                variant: status,
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "center",
                },
                autoHideDuration: duration,
                onClose,
            })
        },
        [enqueueSnackbar]
    )

    return { handleErrorResponse, handleSuccessResponse, handleMessage }
}
